import React, {ComponentType, Suspense, useContext, useEffect, useState} from 'react';
import {useAuth0} from "@auth0/auth0-react";
import {useStateConfig} from "@/hooks/useStateConfig";
import {useAuthStore} from "@/store/auth_store";
import Layout from "@/components/Layout";
import Helpers from "@/helpers/helpers";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import CarecoApi from "@/api/careco-api";
import PermissionsHelpers from "@/helpers/permissions_helpers";
import {FeatureToggle, User} from "@/api/dto/dto";
import {StateConfig} from "@/api/dto/state-config";
import {useLocation, useNavigate} from "react-router-dom";
import useLatestDeployment from "@/hooks/useLatestDeployment";
import useHandleLogout from "@/hooks/useHandleLogout";
import EncounterApi from "@/api/encounter-api";
import WebSocketApi from "@/api/websocket-api";
import ErrorPage from "@/components/pages/error_pages/ErrorPage";
import Loading from "@common/Loading";
import {log} from "@/helpers/logger";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {useRosterStore} from "@/store/roster_store";
import {
  DataPermissionLevel,
  DataPermissionLevelNew,
  DataSource,
  Feature,
  FeatureNew,
  FeaturePermissionLevel
} from "@/api/dto/permissions";
import {NoGroupSelected} from "@common/widgets/WidgetPage.styles";
import {css} from "@emotion/css";
import {sizes} from "patient-ping-remedy/packages/theme";
import {SearchParam} from "@/helpers/search_params";

type FeaturePermissionsCheck = {
  features: {
    level: FeaturePermissionLevel;
    featureNew: FeatureNew;
    feature: Feature;
  }[],
  strategy?: 'AND' | 'OR';
}

type DataPermissionsCheck = {
  level: DataPermissionLevel;
  levelNew: DataPermissionLevelNew;
  dataSource: DataSource;
}

type Props = {
  Component: ComponentType;
  featurePermissionsCheck?: FeaturePermissionsCheck;
  dataPermissionsCheck?: DataPermissionsCheck;
  hasCurrentRosterAccess?: boolean;
  bambooAdminOnly?: boolean;
  featureToggleCheck?: FeatureToggle;
  currentRosterCheck?: boolean;
}

export default function AuthGuard(props: Props) {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const stateConfig = useStateConfig();
  const [dataHydrated, setDataHydrated] = useState(false);
  const authStore = useAuthStore();
  const {carecoApi, updateState} = useContext(CarecoApiContext);
  const navigate = useNavigate();
  const location = useLocation();
  const handleLogout = useHandleLogout();
  const [isError, setIsError] = useState(false);
  const {checkBooleanToggle} = useFeatureToggles();
  const {currentRoster} = useRosterStore();
  useLatestDeployment();

  const featureToggleDisabled =
    props.featureToggleCheck && !checkBooleanToggle(props.featureToggleCheck);

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const getAccessToken = async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      log('error', "Error getting access token: ", error);
      handleLogout();
    }
  };

  const hydrateData = () => {
    getAccessToken().then((accessToken) => {
      if (accessToken && !carecoApi) {
        updateState({
          carecoApi: new CarecoApi(getAccessTokenSilently),
          encounterApi: new EncounterApi(getAccessTokenSilently),
          webSocketApi: new WebSocketApi(),
        });
      }
    });

    if (!authStore.attestationCheck || !authStore.phoneVerificationCheck) {
      const encoded = sessionStorage.getItem('access_check');
      const decoded = encoded ? JSON.parse(atob(encoded)) : null;

      if(!Helpers.isDefined(decoded) || !decoded.attestation_check || !decoded.phone_verification_check) {
        authStore.setReturnToRoute(location.pathname + location.search + location.hash);
        navigate('/attestation');
        return;
      }

      if (decoded && decoded.attestation_check && decoded.phone_verification_check) {
        authStore.setAttestationCheck(decoded.attestation_check);
        authStore.setPhoneVerificationCheck(decoded.phone_verification_check);
      }
    }

    if(!authStore.currentUser) {
      if(user?.sub) {
        carecoApi?.getUser(user.sub)
          .then((response: User) => {
            authStore.setCurrentUser(response);
          }).catch((error) => {
            log('error', error);
            setIsError(true);
          });
      }
    }

    setDataHydrated(true);
  };

  useEffect(() => {
    if (isLoading) return;

    const params = new URL(window.location.toString()).searchParams;

    if(!isAuthenticated) {
      sessionStorage.removeItem('access_check');

      if(params.get('error_description') === "user is blocked") {
        navigate('/unauthorized');
        return;
      }

      loginWithRedirect({
        appState: {
          returnTo: location.pathname + location.search + location.hash,
        },
      });
      return;
    }

    hydrateData();
  }, [carecoApi, user, isLoading, isAuthenticated, getAccessTokenSilently, location.pathname]);

  useEffect(() => {
    if (stateConfig) {
      document.title = stateConfig.branding.name;
      updateFavicon(stateConfig);
    }
  }, [stateConfig]);

  const updateFavicon = (stateConfig: StateConfig) => {
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

    if (icon) {
      icon.href = `${process.env.REACT_APP_CARECO_API_BASE_URL}${stateConfig.branding.favicon}`;
    }
  };

  const checkFeatures = (featurePermissionsCheck: FeaturePermissionsCheck) => {
    const { strategy, features } = featurePermissionsCheck;

    if (strategy === 'OR') {
      return features.some(feature => {
        return (isNewPermissionsEnabled ? PermissionsHelpers.canForUserFeaturePermissionNew(
          authStore.currentUser!,
          feature.level,
          feature.featureNew
        ) :
          PermissionsHelpers.canForUserFeaturePermission(
            authStore.currentUser!,
            feature.level,
            feature.feature
          ));
      });
    }

    return features.every(feature => {
      return (isNewPermissionsEnabled ? PermissionsHelpers.canForUserFeaturePermissionNew(
        authStore.currentUser!,
        feature.level,
        feature.featureNew
      ) :
        PermissionsHelpers.canForUserFeaturePermission(
          authStore.currentUser!,
          feature.level,
          feature.feature
        ));
    });
  };

  const roleAuthorized = () => {
    // there is no role check or permissions check needed
    if(
      !props.bambooAdminOnly &&
       !Helpers.isDefined(props.featurePermissionsCheck) &&
       !Helpers.isDefined(props.dataPermissionsCheck) &&
       !Helpers.isDefined(props.hasCurrentRosterAccess)
    ) {
      return true;
    }

    if(props.currentRosterCheck && props.featurePermissionsCheck) {
      return currentRoster && checkFeatures(props.featurePermissionsCheck);
    }

    if(props.bambooAdminOnly) {
      return PermissionsHelpers.isBamboo(authStore.currentUser!);
    }

    // If hasCurrentRosterAccess is false, don't require roster access
    if(props.hasCurrentRosterAccess === false) {
      return true;
    }

    if(isNewPermissionsEnabled && props.hasCurrentRosterAccess) {
      return currentRoster && PermissionsHelpers.hasRosterAccess(authStore.currentUser!, currentRoster);
    }

    if(props.featurePermissionsCheck) {
      return checkFeatures(props.featurePermissionsCheck);
    }

    if(props.dataPermissionsCheck) {
      return (isNewPermissionsEnabled ? PermissionsHelpers.canForUserDataPermissionNew(
         authStore.currentUser!,
         props.dataPermissionsCheck.levelNew,
         props.dataPermissionsCheck.dataSource
      ): PermissionsHelpers.canForUserDataPermission(
           authStore.currentUser!,
           props.dataPermissionsCheck.level,
           props.dataPermissionsCheck.dataSource
      ));
    }

    // default to false
    return false;
  };

  if(isError) {
    return <ErrorPage/>;
  }

  if (isLoading || !dataHydrated || !authStore.currentUser || !stateConfig) {
    return null;
  }

  if (!!props.hasCurrentRosterAccess && !currentRoster) {
    const isRostersLoading = new URLSearchParams(location.search).has(SearchParam.ROSTER_ID);
    return (
      <Layout>
        {isRostersLoading ? <Loading/> :
          <NoGroupSelected className={css({margin: sizes.medium})}>No group selected</NoGroupSelected>
        }
      </Layout>
    );
  }

  if (!roleAuthorized() || featureToggleDisabled) {
    return (
      <Layout>
        <h2>Unauthorized.</h2>
      </Layout>
    );
  }

  return (
    <Layout>
      <Suspense fallback={<Loading iconSize="2x" />}>
        <props.Component />
      </Suspense>
    </Layout>
  );
}
